// src/pages/departments/MechanicalEngineering.js
import { motion } from "framer-motion";
import {
  Wrench,
  Cog,
  Hammer,
  Thermometer,
  Car,
  Ruler,
  Factory,
  Settings,
} from "lucide-react";

const MechanicalEngineering = () => {
  // Gallery animation variants
  const galleryVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: i * 0.2, duration: 0.5 },
    }),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <section className="relative h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
        <motion.img
          src="/images/mecbg.jpg"
          alt="Department Banner"
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1 }}
        />
        <div className="absolute inset-0 bg-black/50" />
        <motion.div
          className="relative text-center text-white px-6"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-3xl md:text-5xl font-extrabold mb-3">
            Department of Mechanical Engineering
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Excellence in Engineering, Research, and Industrial Innovation
          </p>
        </motion.div>
      </section>

      {/* Content */}
      <div className="px-6 md:px-20 py-16">
        {/* History Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">History</h2>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The <strong>Department of Mechanical Engineering</strong> is one of
            the pioneer departments of the <strong>Federal Polytechnic, Bida</strong>,
            and has played a vital role in driving the institution’s industrial
            and technical innovation. Established at the inception of the
            polytechnic, the department has consistently provided training that
            merges theoretical foundations with hands-on industrial practices.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed mb-4">
            The department offers both <strong>National Diploma (ND)</strong> and{" "}
            <strong>Higher National Diploma (HND)</strong> programmes with
            specializations in <em>Plant Engineering</em>,{" "}
            <em>Automotive Engineering</em>, and <em>Manufacturing Engineering</em>.
            Each specialization is designed to produce engineers capable of
            solving real-world challenges through research, design, and
            innovation.
          </p>
          <p className="text-justify text-gray-600 leading-relaxed">
            Today, the department stands as one of the most research-active units
            within the polytechnic, with staff publishing in Q1 and Q2 indexed
            journals, participating in national projects, and contributing to
            industrial development through consultancy and technical services.
          </p>
        </motion.section>

        {/* Specializations Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Specializations Offered
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Factory size={40} className="text-blue-600" />,
                title: "Plant Engineering",
                desc: "Focuses on power generation systems, mechanical plant design, and industrial maintenance for production efficiency and safety.",
              },
              {
                icon: <Car size={40} className="text-blue-600" />,
                title: "Automotive Engineering",
                desc: "Centers on the design, development, and maintenance of automobiles, with emphasis on diagnostics and performance testing.",
              },
              {
                icon: <Settings size={40} className="text-blue-600" />,
                title: "Manufacturing Engineering",
                desc: "Covers precision manufacturing processes, CNC machining, and innovative design solutions for industrial applications.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl shadow-md p-6 hover:shadow-xl transition transform hover:-translate-y-2"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.5 }}
              >
                <div className="flex items-center gap-4 mb-3">
                  {item.icon}
                  <h3 className="text-xl font-semibold text-gray-800">
                    {item.title}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* HOD Section */}
        <motion.section
          className="grid md:grid-cols-3 gap-8 items-center bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="md:col-span-1 flex justify-center">
            <motion.img
              src="/images/hodmec.jpg"
              alt="Engr. Shehu Abdullahi Bida"
              className="w-40 h-40 rounded-full object-cover shadow-lg border-4 border-blue-200"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
            />
          </div>
          <div className="md:col-span-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Head of Department's Speech
            </h2>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              It is my great pleasure to welcome you to the{" "}
              <strong>Department of Mechanical Engineering</strong> at{" "}
              <strong>Federal Polytechnic, Bida</strong>. Our department has
              maintained a strong legacy of academic excellence, professional
              discipline, and industrial relevance for decades.
            </p>
            <p className="text-justify text-gray-600 leading-relaxed mb-4">
              As engineers, we believe in learning by doing. We provide
              state-of-the-art workshops, laboratories, and facilities that allow
              our students to apply classroom theories to real-world projects,
              preparing them for the challenges of today’s technology-driven
              world.
            </p>
            <p className="text-justify text-gray-600 leading-relaxed">
              I invite you to explore the opportunities we offer and join us in
              building the next generation of innovators who will shape Nigeria’s
              industrial and economic future.
            </p>
            <p className="mt-4 text-blue-700 font-semibold">
              — Engr. Shehu Abdullahi Bida
            </p>
            <p className="text-gray-500">Head, Department of Mechanical Engineering</p>
          </div>
        </motion.section>

        {/* Vision & Mission Section */}
        <motion.section
          className="grid md:grid-cols-2 gap-8 mb-12"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Vision</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To be a <strong>nationally and internationally recognized center of
              excellence</strong> in mechanical engineering education, research,
              and industrial innovation — fostering creativity and advancing
              technology for sustainable development.
            </p>
          </div>
          <div className="bg-white shadow-md rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Mission</h2>
            <p className="text-justify text-gray-600 leading-relaxed">
              To provide world-class education and hands-on training that equips
              students with practical skills, analytical thinking, and leadership
              qualities necessary to become competent engineers capable of solving
              complex technical and societal problems.
            </p>
          </div>
        </motion.section>

        {/* Workshops & Facilities Section */}
        <motion.section
          className="bg-blue-50 shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Workshops & Facilities
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-gray-700">
            {[
              { icon: <Wrench size={32} className="text-blue-600" />, title: "Foundry and Forge Workshop" },
              { icon: <Cog size={32} className="text-blue-600" />, title: "Thermo-Fluids and Fluid Mechanics Laboratory" },
              { icon: <Hammer size={32} className="text-blue-600" />, title: "Strength of Materials Laboratory" },
              { icon: <Thermometer size={32} className="text-blue-600" />, title: "Refrigeration and Air Conditioning Laboratory" },
              { icon: <Car size={32} className="text-blue-600" />, title: "Automotive Workshop" },
              { icon: <Ruler size={32} className="text-blue-600" />, title: "Metrology and Fitting Workshop" },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="flex items-center gap-4 bg-white rounded-xl shadow p-4 hover:shadow-lg transition"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
              >
                {item.icon}
                <p className="font-medium">{item.title}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Gallery Section */}
        <motion.section
          className="mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Department Gallery
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "mec1.jpg",
              "mec2.jpg",
              "mec3.jpg",
              "mec4.jpg",
              "mec5.jpg",
              "mec6.jpg",
            ].map((img, i) => (
              <motion.img
                key={i}
                src={`/images/${img}`}
                alt={`Gallery ${i + 1}`}
                className="w-full h-40 md:h-48 object-cover rounded-lg shadow-md cursor-pointer"
                custom={i}
                variants={galleryVariants}
                initial="hidden"
                whileInView="visible"
                whileHover={{ scale: 1.08, rotate: 1 }}
                viewport={{ once: true }}
              />
            ))}
          </div>
        </motion.section>

        {/* Contact Section */}
        <motion.section
          className="bg-white shadow-md rounded-2xl p-8 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Contact the Department
          </h2>
          <p className="text-gray-600 mb-2">
            <strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Email:</strong> dept.mechanical@fedpolybida.edu.ng
          </p>
          <p className="text-gray-600 mb-2">
            <strong>Phone:</strong> +234 800 000 0000
          </p>
        </motion.section>

        {/* Map Section */}
        <motion.section
          className="rounded-2xl overflow-hidden shadow-md"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <iframe
            title="Department Location"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3926.278884327274!2d6.0125!3d9.0820!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104965e46b2e11f5%3A0xdeadbeef123456!2sFederal%20Polytechnic%20Bida!5e0!3m2!1sen!2sng!4v0000000000000"
            width="100%"
            height="400"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
          ></iframe>
        </motion.section>
      </div>
    </div>
  );
};

export default MechanicalEngineering;
